import React, { useEffect, useState } from "react";
import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  Typography,
  Box,
  Modal,
  Button,
  TextField,
  Pagination,
  IconButton,
  styled,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  ChevronDown,
  Search,
  Plus,
  Edit2,
  HelpCircle,
  MessageCircle,
  X as CloseIcon,
  Sparkles,
  ArrowRight
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AddFaq from "./AddFaq";
import { useFormik } from "formik";
import * as Yup from "yup";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";

// Formik and Yup for form validation
const validationSchema = Yup.object({
  question: Yup.string()
    .required("Question is required")
    .min(5, "Question should be at least 5 characters long"),
  answer: Yup.string()
    .required("Answer is required")
    .min(10, "Answer should be at least 10 characters long"),
});

const StyledAccordion = styled(MuiAccordion)(({ theme }) => ({
  background: 'white',
  borderRadius: '16px !important',
  marginBottom: '12px',
  border: '1px solid #F0F0F5',
  boxShadow: 'none',
  transition: 'all 0.2s ease',
  '&:before': { display: 'none' },
  '&:hover': {
    borderColor: '#E5E7EB',
    bgcolor: '#F9FAFB',
  },
  '&.Mui-expanded': {
    margin: '0 0 16px 0',
    borderColor: '#4F46E5',
    bgcolor: 'white',
  },
}));

const StyledAccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  padding: '12px 20px',
  borderRadius: '16px',
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: '#6B7280',
    transition: 'transform 0.2s ease',
  },
  '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
    color: '#4F46E5',
  }
}));

const ModalContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  maxWidth: '95vw',
  backgroundColor: '#fff',
  borderRadius: '20px',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  padding: '32px',
  outline: 'none',
}));

const AccordionFaq = () => {
  const [faqData, setFaqData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const itemsPerPage = 8;

  useEffect(() => {
    getFaq();
  }, []);

  useEffect(() => {
    const result = faqData.filter(
      (faq) =>
        faq.faq_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.faq_answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredData(result);
    setCurrentPage(1);
  }, [searchQuery, faqData]);

  const getFaq = async () => {
    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getAllFaqs}/me`
      );
      setFaqData(res?.data?.faqs || []);
    } catch (error) {
      console.error("Error fetching FAQ:", error);
    }
  };

  const handleOpen = (faq) => {
    setEditingFaq(faq);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingFaq(null);
  };

  const formik = useFormik({
    initialValues: {
      question: editingFaq?.faq_title || "",
      answer: editingFaq?.faq_answer || "",
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      handleEditFaq({
        ...editingFaq,
        faq_title: values.question,
        faq_answer: values.answer,
      });
    },
  });

  const handleEditFaq = async (updatedFaq) => {
    const data = {
      faq_title: updatedFaq.faq_title,
      faq_answer: updatedFaq.faq_answer,
      faqId: updatedFaq._id,
    };
    try {
      await axiosInstance.put(`${apiEndPointsConfig.updateFaqById}`, data);
      getFaq();
      handleClose();
    } catch (error) {
      console.error("Error updating FAQ:", error);
    }
  };

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ flex: { xs: 1, sm: '0 1 450px' } }}>
          <TextField
            placeholder="Search for answers..."
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: '#F9FAFB',
                borderRadius: "14px",
                height: "48px",
                fontSize: "14px",
                border: "1px solid #E5E7EB",
                "& fieldset": { border: 'none' },
                "&.Mui-focused": {
                  bgcolor: 'white',
                  border: "1px solid #4F46E5",
                  boxShadow: '0 4px 12px rgba(79, 70, 229, 0.1)',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#9CA3AF" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <AddFaq getFaq={getFaq} />
      </Box>

      <AnimatePresence mode="wait">
        {paginatedData.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Box sx={{
              mt: 6,
              display: "flex",
              flexDirection: 'column',
              alignItems: "center",
              textAlign: "center",
              gap: 2,
              p: 6,
              bgcolor: '#F9FAFB',
              borderRadius: '20px',
              border: '1px dashed #E5E7EB'
            }}>
              <HelpCircle size={40} color="#9CA3AF" />
              <Box>
                <Typography variant="subtitle1" fontWeight={700} color="#374151">
                  No FAQs found
                </Typography>
                <Typography variant="body2" color="#6B7280">
                  Try adjusting your search terms or add a new question.
                </Typography>
              </Box>
            </Box>
          </motion.div>
        ) : (
          <Box>
            {paginatedData.map((faq, index) => (
              <motion.div
                key={faq._id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <StyledAccordion>
                  <StyledAccordionSummary expandIcon={<ChevronDown size={18} />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <HelpCircle size={18} color="#4F46E5" />
                      <Typography variant="body1" fontWeight={600} color="#111827" sx={{ fontSize: '15px' }}>
                        {faq?.faq_title}
                      </Typography>
                    </Box>
                  </StyledAccordionSummary>
                  <MuiAccordionDetails sx={{ px: 2.5, pb: 3, pt: 0 }}>
                    <Box sx={{
                      p: 2,
                      bgcolor: '#F9FAFB',
                      borderRadius: '12px',
                      border: '1px solid #F3F4F6',
                      mb: 2
                    }}>
                      <Typography
                        sx={{
                          color: "#4B5563",
                          lineHeight: 1.6,
                          fontSize: '14px',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {faq?.faq_answer}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: 'flex-end' }}>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<Edit2 size={14} />}
                        sx={{
                          textTransform: "none",
                          color: "#4F46E5",
                          fontWeight: 600,
                          fontSize: '13px',
                          '&:hover': { bgcolor: 'rgba(79, 70, 229, 0.05)' }
                        }}
                        onClick={() => handleOpen(faq)}
                      >
                        Edit Solution
                      </Button>
                    </Box>
                  </MuiAccordionDetails>
                </StyledAccordion>
              </motion.div>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={Math.ceil(filteredData.length / itemsPerPage)}
                page={currentPage}
                onChange={(e, v) => setCurrentPage(v)}
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: '8px',
                    fontWeight: 600,
                    margin: '0 2px',
                    "&.Mui-selected": {
                      bgcolor: '#4F46E5',
                      color: 'white',
                      "&:hover": { bgcolor: '#4338CA' }
                    },
                  }
                }}
              />
            </Box>
          </Box>
        )}
      </AnimatePresence>

      <Modal open={open} onClose={handleClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <ModalContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={800} color="#111827">Edit Solution</Typography>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon size={18} color="#6B7280" />
              </IconButton>
            </Box>

            <form onSubmit={formik.handleSubmit}>
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="caption" fontWeight={700} color="#6B7280" sx={{ mb: 1, display: 'block', textTransform: 'uppercase' }}>
                  Question
                </Typography>
                <TextField
                  name="question"
                  fullWidth
                  variant="outlined"
                  value={formik.values.question}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.question && Boolean(formik.errors.question)}
                  helperText={formik.touched.question && formik.errors.question}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: '12px',
                      bgcolor: '#F9FAFB',
                      fontSize: '14px',
                      "& fieldset": { borderColor: '#E5E7EB' },
                      "&.Mui-focused fieldset": { borderColor: '#4F46E5' }
                    }
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" fontWeight={700} color="#6B7280" sx={{ mb: 1, display: 'block', textTransform: 'uppercase' }}>
                  Solution
                </Typography>
                <TextField
                  name="answer"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={5}
                  value={formik.values.answer}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.answer && Boolean(formik.errors.answer)}
                  helperText={formik.touched.answer && formik.errors.answer}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: '12px',
                      bgcolor: '#F9FAFB',
                      fontSize: '14px',
                      "& fieldset": { borderColor: '#E5E7EB' },
                      "&.Mui-focused fieldset": { borderColor: '#4F46E5' }
                    }
                  }}
                />
              </Box>

              <Button
                variant="contained"
                fullWidth
                type="submit"
                sx={{
                  bgcolor: "#4F46E5",
                  borderRadius: '12px',
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: 'none',
                  "&:hover": { bgcolor: "#4338CA" }
                }}
              >
                Save Changes
              </Button>
            </form>
          </ModalContainer>
        </motion.div>
      </Modal>
    </Box>
  );
};

export default AccordionFaq;
